(() => {
  if (customElements.get('candle-club-builder')) return;
  const STEPS = ['box', 'scents', 'delivery', 'extras', 'review'];
  class CandleClubBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller || !this.querySelector('[data-cc-config]')) return;
      this.controller = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-cc-config]').textContent);
      this.state = { box: null, scents: [], months: 1, addons: {} };
      this.step = 'box';
      this.curated = [];
      this.ready = false;
      this.busy = false;
      this.loadVersion = 0;
      this.storageKey = `cc-builder-v3:${Object.values(this.config.boxes).map(box => `${box.productId}-${box.variantId}`).join(':')}:${this.dataset.sectionId}`;
      this.scentCards = [...this.querySelectorAll('[data-catalog-scope="scents"] [data-product]')];
      this.addonCards = [...this.querySelectorAll('[data-catalog-scope="extras"] [data-product]')];
      this.filters = { scents: { family: 'all' }, extras: { family: 'all' } };
      this.initFamilyFilters();
      const header = document.querySelector('.section-header');
      const hasStickyHeader = Boolean(document.querySelector('sticky-header'));
      this.header = header;
      // Reserve space for the header's settled (post-transition) height rather than sampling
      // its bounding box mid-animation — sampling mid-transition is what left a brief gap
      // while the header was still sliding into view. `.cc-header-backdrop` covers whatever
      // part of that reserved space the real header hasn't visually reached yet.
      // Driven by the header's own class changes (below), not by 'scroll': our own jumps in
      // scrollToTop() fire a scroll event before the theme's reveal()/hide() have run, so a
      // scroll-triggered read here would see the *previous* hidden/shown state and stomp the
      // proactive reservation scrollToTop() just made.
      const positionProgress = () => {
        if (!header || !hasStickyHeader) { this.style.setProperty('--cc-header-bottom', '0px'); return; }
        const hidden = header.classList.contains('shopify-section-header-hidden');
        const bottom = hidden ? 0 : Math.max(0, header.offsetHeight - 1);
        this.style.setProperty('--cc-header-bottom', `${bottom}px`);
      };
      window.addEventListener('resize', positionProgress, { signal: this.controller.signal });
      if (header) {
        this.headerObserver = new MutationObserver(positionProgress);
        this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
      }
      positionProgress();
      this.addEventListener('click', event => this.click(event), { signal: this.controller.signal });
      this.addEventListener('keydown', event => this.radioKeys(event), { signal: this.controller.signal });
      // popstate also fires for in-page #anchor links; only react when the step itself changed.
      window.addEventListener('popstate', () => { if (this.requestedStep() !== this.step) this.go(this.requestedStep(), false); }, { signal: this.controller.signal });
      // Links elsewhere on the page that point into the builder (e.g. "Build your box" → #first-header)
      // glide to the top of the builder instead of the browser's own anchor jump.
      document.addEventListener('click', event => {
        const link = event.target.closest('a[href*="#"]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash) return;
        let target;
        try { target = document.querySelector(decodeURIComponent(url.hash)); } catch (_) { return; }
        if (!target || !this.contains(target)) return;
        event.preventDefault();
        this.scrollToTop(true);
      }, { signal: this.controller.signal });
      ['wheel', 'touchstart', 'keydown'].forEach(type => window.addEventListener(type, () => { if (this.scrollFrame) this.stopScroll(); }, { passive: true, signal: this.controller.signal }));
      this.barObserver = new ResizeObserver(() => {
        this.style.setProperty('--cc-bar-height', `${this.querySelector('[data-purchase]').getBoundingClientRect().height}px`);
      });
      this.barObserver.observe(this.querySelector('[data-purchase]'));
      // Only show the fixed purchase bar while the builder itself is on screen,
      // so it doesn't sit over the landing content above the builder.
      const purchase = this.$('[data-purchase]');
      this.shellObserver = new IntersectionObserver(([entry]) => {
        // Once the builder has been scrolled past (its bottom edge is above the viewport),
        // keep the bar showing rather than hiding it again over the footer.
        const passedBuilder = entry.boundingClientRect.bottom <= 0;
        purchase.classList.toggle('is-visible', entry.isIntersecting || passedBuilder || purchase.contains(document.activeElement));
      }, { rootMargin: '0px 0px -35% 0px' });
      this.shellObserver.observe(this.$('.cc-shell'));
      purchase.addEventListener('focusin', () => purchase.classList.add('is-visible'), { signal: this.controller.signal });
      this.restore();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.barObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.shellObserver?.disconnect();
      this.stopScroll();
      this.loadVersion++;
    }
    $(selector) { return this.querySelector(selector); }
    initFamilyFilters() {
      for (const scope of ['scents', 'extras']) {
        const container = this.$(`[data-catalog-scope="${scope}"]`);
        const values = new Set();
        container.querySelectorAll('[data-product]').forEach(card => (card.dataset.family || '').split('|').filter(Boolean).forEach(value => values.add(value.trim())));
        [...values].sort().forEach(value => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'cc-chip'; button.dataset.filter = 'family'; button.dataset.value = value;
          button.setAttribute('aria-pressed', 'false'); button.textContent = value.replace(/\b\w/g, letter => letter.toUpperCase());
          container.querySelector('.cc-chips').append(button);
        });
      }
    }
    openSwap(id, trigger) {
      const dialog = this.$('[data-swap-dialog]');
      const list = dialog.querySelector('[data-swap-options]');
      const desired = this.scentCards.find(card => card.dataset.variant === id);
      dialog.querySelector('[data-swap-product-title]').textContent = desired.dataset.title;
      dialog.querySelector('[data-swap-description]').textContent = 'Which candle do you want to remove from your current selection?';
      list.replaceChildren();
      this.state.scents.forEach(selectedId => {
        const card = this.scentCards.find(item => item.dataset.variant === selectedId);
        const button = document.createElement('button'); button.type = 'button'; button.className = 'cc-swap-option';
        if (card.dataset.image) button.append(this.thumbnail(card));
        const text = document.createElement('span'); text.textContent = card.dataset.title; button.append(text);
        button.addEventListener('click', () => {
          this.state.scents = this.state.scents.map(value => value === selectedId ? id : value);
          this.error(); this.render(); dialog.close(); trigger.focus();
          this.$('[data-live]').textContent = `${card.dataset.title} replaced with ${desired.dataset.title}.`;
        }, { once: true });
        list.append(button);
      });
      dialog.showModal();
    }
    money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency }).format(cents / 100); }
    box() { return this.config.boxes[this.state.box]; }
    requestedStep() { return new URL(location.href).searchParams.get('step') || 'box'; }
    plan(months = this.state.months) {
      return this.box()?.plans.find(plan => {
        const name = plan.name.toLowerCase();
        if (months === 1) return /monthly|every month|every 1 month|30 day|1 month/.test(name) && !/2 month|3 month|quarter/.test(name);
        if (months === 2) return /2 month|two month|60 day|other month/.test(name);
        return /3 month|three month|90 day|quarter/.test(name);
      });
    }
    price() { return this.plan()?.price ?? this.box()?.price ?? 0; }
    cadence() { return this.state.months === 1 ? 'every month' : `every ${this.state.months} months`; }
    async restore() {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.storageKey)); } catch (_) { /* Storage may be disabled. */ }
      if (saved && this.config.boxes[saved.box]?.available) {
        this.state.months = [1, 2, 3].includes(saved.months) ? saved.months : 1;
        for (const card of this.addonCards) {
          const quantity = saved.addons?.[card.dataset.variant];
          if (Number.isSafeInteger(quantity) && quantity > 0) this.state.addons[card.dataset.variant] = quantity;
        }
        await this.chooseBox(saved.box, Array.isArray(saved.scents) ? saved.scents : undefined);
      }
      this.go(this.requestedStep(), false, false);
    }
    persist() {
      const serialized = JSON.stringify(this.state);
      try { sessionStorage.setItem(this.storageKey, serialized); } catch (_) { /* The flow also works without storage. */ }
    }
    error(message = '', retry = false) {
      this.$('[data-error]').textContent = message;
      this.$('[data-error]').hidden = !message;
      this.$('[data-retry]').hidden = !retry;
    }
    completeScents() { return this.ready && this.state.scents.length === this.box()?.quota; }
    allowed(step) {
      if (step === 'box') return true;
      if (!this.box()) return false;
      if (step === 'scents') return true;
      if (!this.completeScents()) return false;
      return step === 'delivery' || Boolean(this.plan());
    }
    go(step, push = true, focus = true) {
      if (this.busy) return;
      if (!STEPS.includes(step)) step = 'box';
      if (!this.allowed(step)) step = !this.box() ? 'box' : !this.completeScents() ? 'scents' : 'delivery';
      this.step = step;
      const url = new URL(location.href);
      if (url.searchParams.get('step') !== step) {
        url.searchParams.set('step', step);
        history[push ? 'pushState' : 'replaceState']({}, '', url);
      }
      this.render();
      if (focus) {
        const heading = this.$(`[data-step="${step}"] .cc-heading [tabindex="-1"]`);
        heading?.focus({ preventScroll: true });
        this.scrollToTop();
      }
    }
    scrollToTop(smooth = false) {
      this.stopScroll();
      // Move to the top of the builder (progress bar + step heading), leaving room for the
      // theme header when it will be showing: always-sticky, or revealed by scrolling up.
      // Step changes jump instantly; only links into the builder from the page glide.
      const alwaysSticky = document.querySelector('sticky-header[data-sticky-type="always"]');
      const stickyHeader = document.querySelector('sticky-header');
      const headerHeight = stickyHeader && this.header ? this.header.offsetHeight : 0;
      const scrollingUp = this.getBoundingClientRect().top + window.scrollY - headerHeight < window.scrollY;
      const revealingHeader = alwaysSticky || (stickyHeader && scrollingUp);
      // Reserve the header's space right away, before the jump paints, rather than waiting
      // to react to the theme's own (one rAF frame later) header reveal — otherwise the
      // first painted frame briefly has nothing reserved yet.
      if (revealingHeader) this.style.setProperty('--cc-header-bottom', `${Math.max(0, headerHeight - 1)}px`);
      const target = () => {
        let top = this.getBoundingClientRect().top + window.scrollY;
        if (revealingHeader) top -= headerHeight;
        return Math.max(0, Math.min(Math.round(top), document.documentElement.scrollHeight - window.innerHeight));
      };
      const root = document.documentElement;
      // The theme's custom CSS sets `html { scroll-behavior: smooth }`, which would fight the
      // scrollTo calls below; the animation provides the smoothing when wanted.
      this.savedScrollBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      const from = window.scrollY;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = !smooth || reduceMotion ? 0 : Math.min(900, Math.max(400, Math.abs(target() - from) * 0.45));
      const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      let start = null;
      let settled = 0;
      const step = now => {
        start ??= now;
        const progress = duration ? Math.min(1, (now - start) / duration) : 1;
        // Re-aim every frame: the header slides and images load while the page moves.
        const top = Math.round(from + (target() - from) * ease(progress));
        if (Math.abs(window.scrollY - top) > 1) window.scrollTo({ top, left: 0, behavior: 'auto' });
        // After arriving, hold the position for a few frames so late layout shifts can't pull it away.
        if (progress < 1 || ++settled < 3) this.scrollFrame = requestAnimationFrame(step);
        else this.stopScroll();
      };
      // An instant jump happens now, before paint, so a shorter step never flashes the footer.
      if (duration) this.scrollFrame = requestAnimationFrame(step);
      else step(performance.now());
    }
    stopScroll() {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
      if (this.savedScrollBehavior !== undefined) {
        document.documentElement.style.scrollBehavior = this.savedScrollBehavior;
        this.savedScrollBehavior = undefined;
      }
    }
    async chooseBox(key, restoredScents) {
      if (!this.config.boxes[key]?.available || this.busy) return;
      if (this.state.box === key && this.ready && restoredScents === undefined) return;
      const version = ++this.loadVersion;
      this.state.box = key;
      this.state.scents = [];
      this.ready = false;
      this.curated = [];
      this.loading = true;
      this.error();
      if (!this.plan()) this.state.months = [1, 2, 3].find(month => this.plan(month)) || 1;
      this.render();
      try {
        // Recharge is initialized asynchronously by the existing theme layout.
        for (let attempt = 0; attempt < 40 && !window.recharge?.cdn?.getCDNBundleSettings; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 200));
          if (version !== this.loadVersion) return;
        }
        if (!window.recharge?.cdn?.getCDNBundleSettings) throw new Error('Your box could not load. Please try again.');
        const settings = await window.recharge.cdn.getCDNBundleSettings(String(this.box().productId));
        if (version !== this.loadVersion) return;
        const variant = (settings.variants || []).find(item => String(item.external_variant_id) === String(this.box().variantId));
        if (!variant) throw new Error('This box is temporarily unavailable. Please try again later.');
        const expected = this.box().quota;
        if (Number(variant.items_count) !== expected) throw new Error('This box’s candle quantity does not match its subscription setup. Please contact us for help.');
        if (!(variant.option_sources || []).some(source => String(source.collection_id) === String(this.box().collectionId))) throw new Error('This box’s fragrance collection does not match its subscription setup. Please contact us for help.');
        const curated = [];
        for (const item of variant.selection_defaults || []) {
          const id = String(item.external_variant_id);
          const quantity = Number(item.quantity || 1);
          if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > expected) throw new Error('This box’s included items are being updated.');
          if (this.box().candleVariantIds.includes(id)) {
            // The picker uses distinct scents; preserve curation order.
            if (!curated.includes(id) && this.scentCards.some(card => card.dataset.variant === id)) curated.push(id);
          }
        }
        this.curated = curated.slice(0, this.box().quota);
        const catalog = this.$('[data-catalog-scope="scents"] .cc-catalog');
        const orderedCards = [...this.scentCards].sort((a, b) => {
          const aIndex = this.curated.indexOf(a.dataset.variant);
          const bIndex = this.curated.indexOf(b.dataset.variant);
          return (aIndex < 0 ? Infinity : aIndex) - (bIndex < 0 ? Infinity : bIndex);
        });
        catalog.append(...orderedCards);
        const valid = new Set(this.scentCards.filter(card => this.box().candleVariantIds.includes(card.dataset.variant)).map(card => card.dataset.variant));
        this.state.scents = restoredScents !== undefined
          ? [...new Set(restoredScents.filter(id => typeof id === 'string' && valid.has(id)))].slice(0, this.box().quota)
          : [...this.curated];
        this.ready = true;
        if (!this.completeScents()) this.error('Choose your favorite scents to finish filling your box.');
      } catch (error) {
        if (version !== this.loadVersion) return;
        this.ready = false;
        this.error(error.message || 'Your box could not load. Please try again.', true);
      } finally {
        if (version === this.loadVersion) { this.loading = false; this.render(); }
      }
    }
    click(event) {
      const scentCard = event.target.closest('[data-catalog-scope="scents"] [data-product]');
      if (scentCard && !event.target.closest('button, a, input, select, textarea, label')) {
        scentCard.querySelector('[data-scent-toggle]').click();
        return;
      }
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled || this.busy) return;
      if (button.dataset.box) { this.chooseBox(button.dataset.box); return; }
      if (button.hasAttribute('data-swap-cancel')) { this.$('[data-swap-dialog]').close(); return; }
      if (button.dataset.go) { this.go(button.dataset.go); return; }
      if (button.hasAttribute('data-retry')) { this.chooseBox(this.state.box); return; }
      if (button.hasAttribute('data-back')) { this.go(STEPS[STEPS.indexOf(this.step) - 1]); return; }
      if (button.hasAttribute('data-next')) {
        if (this.step === 'review') this.submit();
        else this.go(STEPS[STEPS.indexOf(this.step) + 1]);
        return;
      }
      if (button.hasAttribute('data-scent-toggle')) {
        const id = button.closest('[data-product]').dataset.variant;
        if (this.state.scents.includes(id)) this.state.scents = this.state.scents.filter(value => value !== id);
        else if (this.state.scents.length < this.box().quota && this.ready) this.state.scents.push(id);
        else if (this.ready && this.box().quota === 1) this.state.scents = [id];
        else if (this.ready) { this.openSwap(id, button); return; }
        this.error();
      } else if (button.dataset.months) {
        const months = Number(button.dataset.months);
        if (this.plan(months)) this.state.months = months;
      } else if (button.hasAttribute('data-addon-add') || button.hasAttribute('data-quantity')) {
        const id = button.closest('[data-product]').dataset.variant;
        const quantity = (this.state.addons[id] || 0) + Number(button.dataset.quantity || 1);
        if (Number.isSafeInteger(quantity) && quantity > 0) this.state.addons[id] = quantity;
        else delete this.state.addons[id];
        // Move focus when Add is replaced by a stepper (and back at zero).
        const card = button.closest('[data-product]');
        this.render();
        if (button.hidden || button.closest('[hidden]')) card.querySelector(quantity > 0 ? '[data-quantity="1"]' : '[data-addon-add]').focus();
        return;
      } else if (button.dataset.filter) {
        const scope = button.closest('[data-catalog-scope]').dataset.catalogScope;
        this.filters[scope][button.dataset.filter] = button.dataset.value;
      } else return;
      this.render();
      this.$('[data-live]').textContent = `${this.state.scents.length} of ${this.box()?.quota || 0} scents selected`;
    }
    radioKeys(event) {
      const button = event.target.closest('[role="radio"]');
      if (!button || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const radios = [...button.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]')].filter(item => !item.disabled);
      let index = radios.indexOf(button);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = radios.length - 1;
      else index = (index + (/Right|Down/.test(event.key) ? 1 : -1) + radios.length) % radios.length;
      radios[index]?.focus();
      radios[index]?.click();
    }
    renderFilters(scope, cards) {
      const container = this.$(`[data-catalog-scope="${scope}"]`);
      const filters = this.filters[scope];
      container.querySelectorAll('[data-filter]').forEach(button => button.setAttribute('aria-pressed', String(filters[button.dataset.filter] === button.dataset.value)));
      let count = 0;
      cards.forEach(card => {
        const eligible = scope !== 'scents' || Boolean(this.box()?.candleVariantIds.includes(card.dataset.variant));
        const match = eligible && Object.entries(filters).every(([group, value]) => {
          if (value === 'all') return true;
          const raw = card.dataset[group] || '';
          return raw.split(/[|,]/).some(tag => {
            const normalized = tag.trim().replace(/^(scent family|fragrance family|family)\s*[:_]\s*/, '').replace(/autumn/g, 'fall');
            return normalized === value;
          });
        });
        card.hidden = !match;
        if (match) count++;
      });
      container.querySelector('[data-result-count]').textContent = `${count} scent${count === 1 ? '' : 's'}`;
      container.querySelector('[data-empty-results]').hidden = count > 0;
    }
    render() {
      const box = this.box();
      const index = STEPS.indexOf(this.step);
      this.querySelectorAll('[data-step]').forEach(panel => panel.hidden = panel.dataset.step !== this.step);
      this.querySelectorAll('[data-progress]').forEach((item, i) => {
        item.classList.toggle('is-complete', i < index);
        if (i === index) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
        item.querySelector('[data-bubble]').textContent = i < index ? '✓' : i + 1;
        item.querySelector('[data-completed]').textContent = i < index ? ' completed' : '';
        item.querySelector('button').disabled = this.busy || !this.allowed(STEPS[i]);
      });
      this.querySelectorAll('[data-box]').forEach(button => {
        const selected = button.dataset.box === this.state.box;
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected || (!box && button.dataset.box === 'candle') ? 0 : -1;
        button.disabled = this.busy || !this.config.boxes[button.dataset.box].available;
        button.querySelector('[data-box-label]').textContent = selected ? 'Selected' : button.disabled && !this.busy ? 'Unavailable' : 'Select this box';
      });
      this.$('[data-loading]').hidden = !this.loading;
      this.$('[data-curation-quota]').textContent = box?.quota === 1 ? 'one' : 'three';
      this.scentCards.forEach(card => {
        const selected = this.state.scents.includes(card.dataset.variant);
        const curated = this.curated.includes(card.dataset.variant);
        card.classList.toggle('is-selected', selected);
        const button = card.querySelector('[data-scent-toggle]');
        button.textContent = selected ? '✓ Selected' : 'Select';
        button.setAttribute('aria-pressed', String(selected));
        button.setAttribute('aria-label', `${selected ? 'Remove' : 'Add'} ${card.dataset.title}`);
        button.disabled = this.busy || !this.ready;
        const badge = card.querySelector('[data-curated-badge]');
        badge.hidden = !curated;
        badge.textContent = selected ? 'Our pick' : 'Removed';
        badge.classList.toggle('is-removed', !selected);
      });
      this.querySelectorAll('[data-months]').forEach(button => {
        const months = Number(button.dataset.months);
        const plan = this.plan(months);
        button.disabled = this.busy || !plan;
        button.setAttribute('aria-checked', String(Boolean(plan) && months === this.state.months));
        button.tabIndex = plan && months === this.state.months ? 0 : -1;
        button.querySelector('[data-frequency-price]').textContent = plan ? `${this.money(plan.price)} / box` : 'Currently unavailable';
      });
      this.addonCards.forEach(card => {
        const qty = this.state.addons[card.dataset.variant] || 0;
        card.classList.toggle('is-selected', qty > 0);
        card.querySelector('[data-addon-add]').hidden = qty > 0;
        card.querySelector('[data-stepper]').hidden = !qty;
        card.querySelector('[data-quantity-value]').textContent = qty;
        card.querySelectorAll('button').forEach(button => button.disabled = this.busy);
      });
      this.renderFilters('scents', this.scentCards);
      this.renderFilters('extras', this.addonCards);
      this.renderSummary();
      if (this.step === 'review') this.renderReview();
      this.persist();
    }
    addonTotals() {
      let quantity = 0, total = 0, saving = 0;
      this.addonCards.forEach(card => {
        const qty = this.state.addons[card.dataset.variant] || 0;
        quantity += qty;
        total += qty * this.config.memberPrice;
        saving += qty * Math.max(0, Number(card.dataset.retail) - this.config.memberPrice);
      });
      return { quantity, total, saving };
    }
    renderSummary() {
      const box = this.box();
      const addons = this.addonTotals();
      const label = this.$('[data-summary-label]');
      const summary = this.$('[data-summary]');
      const note = this.$('[data-summary-note]');
      const next = this.$('[data-next]');
      label.classList.remove('cc-savings');
      label.textContent = 'Your box';
      label.classList.toggle('cc-box-label', this.step === 'box' || this.step === 'delivery');
      summary.textContent = box ? `${box.title} · ${this.money(this.price())} per box` : 'Select a box';
      note.textContent = '';
      label.hidden = false; summary.hidden = false;
      this.$('[data-back]').hidden = this.step === 'box';
      this.$('[data-back]').disabled = this.busy;
      next.classList.toggle('cc-button--final', this.step === 'review');
      next.classList.toggle('cc-button--outline', this.step === 'extras' && !addons.quantity);
      const tray = this.$('[data-tray]');
      tray.hidden = this.step !== 'scents';
      this.$('.cc-purchase__summary').classList.toggle('cc-has-tray', !tray.hidden);
      tray.replaceChildren();
      if (this.step === 'box') {
        next.textContent = 'Continue';
        next.disabled = !box || this.loading || !this.ready;
      } else if (this.step === 'scents') {
        label.textContent = ''; label.hidden = true;
        summary.textContent = `${this.state.scents.length} of ${box.quota} selected`;
        note.textContent = '';
        for (let i = 0; i < box.quota; i++) {
          const slot = document.createElement('span');
          const card = this.scentCards.find(card => card.dataset.variant === this.state.scents[i]);
          if (card?.dataset.image) slot.append(this.thumbnail(card)); else slot.textContent = '+';
          tray.append(slot);
        }
        next.textContent = 'Continue';
        next.disabled = !this.completeScents();
      } else if (this.step === 'delivery') {
        summary.textContent += ` · ${this.cadence()}`;
        note.textContent = '';
        next.textContent = 'Continue';
        next.disabled = !this.plan();
      } else if (this.step === 'extras') {
        label.textContent = 'Your extras';
        if (addons.quantity) {
          const amount = document.createElement('span');
          amount.className = 'cc-savings__amount';
          amount.textContent = this.money(addons.saving);
          label.replaceChildren(document.createTextNode('You’re saving '), amount, document.createTextNode(''));
        }
        label.classList.toggle('cc-savings', addons.quantity > 0);
        summary.textContent = ''; summary.hidden = true;
        note.textContent = '';
        next.textContent = addons.quantity ? 'Continue' : 'Skip this step';
        next.disabled = !this.completeScents() || !this.plan();
      } else {
        label.textContent = 'Total';
        summary.textContent = this.money(this.price() + addons.total);
        note.textContent = `Box: ${this.money(this.price())} ${this.cadence()}${addons.quantity ? `\nExtras: ${this.money(addons.total)} · one-time order` : ''}`;
        next.textContent = 'Checkout';
        next.disabled = !this.completeScents() || !this.plan();
      }
      note.hidden = !note.textContent;
      if (this.busy) { next.disabled = true; next.textContent = this.step === 'review' ? 'Going to checkout…' : 'Adding…'; }
      next.setAttribute('aria-disabled', String(next.disabled));
    }
    thumbnail(card) {
      const image = document.createElement('img');
      image.src = card.dataset.image;
      image.alt = card.dataset.title;
      image.width = 52;
      image.height = 52;
      return image;
    }
    renderReview() {
      const review = this.$('[data-review]');
      review.replaceChildren();
      const row = (title, description, step, editLabel, cards = []) => {
        const element = document.createElement('div'); element.className = 'cc-review__row';
        const content = document.createElement('div');
        const heading = document.createElement('h3'); heading.textContent = title;
        const text = document.createElement('p'); text.textContent = description;
        content.append(heading, text);
        if (cards.length) {
          const list = document.createElement('ul'); list.className = 'cc-review__items';
          cards.forEach(({ card, qty }) => {
            const item = document.createElement('li');
            if (card.dataset.image) item.append(this.thumbnail(card));
            const name = document.createElement('span'); name.textContent = `${card.dataset.title}${qty > 1 ? ` × ${qty}` : ''}`;
            item.append(name); list.append(item);
          });
          content.append(list);
        }
        if (step) {
          const edit = document.createElement('button'); edit.type = 'button'; edit.className = 'cc-link'; edit.dataset.go = step; edit.textContent = editLabel; edit.disabled = this.busy;
          element.append(content, edit);
        } else {
          element.append(content);
        }
        review.append(element);
      };
      row(this.box().title, `${this.box().quota} candle${this.box().quota > 1 ? 's' : ''} per box · ${this.money(this.price())}`, 'box', 'Edit');
      row('Scents', '', 'scents', 'Edit', this.state.scents.map(id => ({ card: this.scentCards.find(card => card.dataset.variant === id), qty: 1 })));
      row('Delivery', `${this.cadence()} · ${this.money(this.price())} per box`, 'delivery', 'Edit');
      row('Extras', this.addonTotals().quantity ? `${this.money(this.addonTotals().total)} · one-time order` : 'No extras added', 'extras', 'Edit', this.addonCards.filter(card => this.state.addons[card.dataset.variant]).map(card => ({ card, qty: this.state.addons[card.dataset.variant] })));
      row('Free with every box', 'Free samples and matches with every box');
    }
    async submit() {
      if (this.busy || !this.completeScents() || !this.plan()) return;
      this.busy = true;
      this.error();
      this.render();
      let added = false;
      try {
        const api = window.recharge?.bundle;
        if (!api?.getBundleId) throw new Error('Your membership could not load. Refresh the page and try again.');
        const selections = this.state.scents.map(id => {
          const card = this.scentCards.find(card => card.dataset.variant === id);
          return { collectionId: String(this.box().collectionId), externalProductId: card.dataset.productId, externalVariantId: id, quantity: 1 };
        });
        const bundle = { externalProductId: String(this.box().productId), externalVariantId: String(this.box().variantId), selections };
        if (api.validateBundleSelection) {
          const validation = await api.validateBundleSelection(bundle);
          if (validation === false || validation?.valid === false) throw new Error('Your box could not be confirmed. Please review your selections and try again.');
        } else if (api.validateBundle) {
          const validation = await api.validateBundle(bundle);
          if (validation !== true) throw new Error('Your box could not be confirmed. Please review your selections and try again.');
        }
        const bundleId = await api.getBundleId(bundle);
        if (!bundleId) throw new Error('Your box could not be confirmed. Please try again.');
        const candles = this.state.scents.map(id => this.scentCards.find(card => card.dataset.variant === id).dataset.title).join(', ');
        const items = [{ id: Number(this.box().variantId), quantity: 1, selling_plan: Number(this.plan().id), properties: { _rb_id: bundleId, Candles: candles } }];
        this.addonCards.forEach(card => {
          const quantity = this.state.addons[card.dataset.variant];
          if (quantity) items.push({ id: Number(card.dataset.variant), quantity, parent_id: Number(this.box().variantId), properties: { _cc_addon: 'true' } });
        });
        const base = window.Shopify?.routes?.root || '/';
        // Upcart intercepts every cart/add request and opens its drawer; this purchase
        // goes straight to checkout, so tell it to ignore this add.
        const upcart = { skip: window.upcartShouldSkipAddToCart, open: window.upcartShouldOpenOnAddToCartBackgroundNetworkCall };
        window.upcartShouldSkipAddToCart = () => true;
        window.upcartShouldOpenOnAddToCartBackgroundNetworkCall = false;
        const response = await fetch(`${base}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!response.ok) {
          window.upcartShouldSkipAddToCart = upcart.skip;
          if (upcart.open === undefined) delete window.upcartShouldOpenOnAddToCartBackgroundNetworkCall;
          else window.upcartShouldOpenOnAddToCartBackgroundNetworkCall = upcart.open;
          const body = await response.json().catch(() => ({}));
          throw new Error(body.description || body.message || 'Could not add your membership. Please try again.');
        }
        added = true;
        try { sessionStorage.removeItem(this.storageKey); } catch (_) { /* Optional storage. */ }
        // Once Shopify accepts the add, never offer a retry that duplicates it.
        this.$('[data-live]').textContent = 'Your membership has been added. Taking you to checkout.';
        location.assign(`${base}checkout`);
      } catch (error) {
        this.error(error.message || 'Could not add your membership. Please try again.');
      } finally {
        if (!added) { this.busy = false; this.render(); }
      }
    }
  }
  customElements.define('candle-club-builder', CandleClubBuilder);
})();
